import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { TIME_RANGE_MS, type TimeRangePreset } from "@/lib/time-range";

/**
 * Header monitoring reads (task 192): a site's reported policies and a
 * monitored page's scheduled scans. All readable on every plan, so a tenant
 * that downgraded keeps its history.
 */

/** Scans fetched per page of history. */
export const SCAN_PAGE_SIZE = 30;

export function useCspPolicies(siteId: string | undefined, pageId?: string) {
  return useQuery({
    queryKey: ["sites", siteId ?? "", "csp-policies", pageId ?? null],
    queryFn: () =>
      api.pages.cspPolicies(siteId!, pageId ? { pageId } : undefined),
    enabled: !!siteId,
    // Built from reports over the tenant's retention; a minute is fresh.
    staleTime: 60_000,
  });
}

/**
 * Every site's policies at once, for `Policy > Changes` under "all sites"
 * (ADR 013). One query per site, sharing the keys `useCspPolicies` uses, so
 * scoping to a site reads what this already fetched.
 */
export function useCspPoliciesForSites(siteIds: readonly string[]) {
  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["sites", siteId, "csp-policies", null],
      queryFn: () => api.pages.cspPolicies(siteId),
      staleTime: 60_000,
    })),
  });
}

/** A page's scans, newest first, a page of history at a time by `nextBefore`. */
export function usePageScans(
  siteId: string | undefined,
  pageId: string | undefined,
) {
  return useInfiniteQuery({
    queryKey: ["sites", siteId ?? "", "pages", pageId ?? "", "scans"],
    queryFn: ({ pageParam }) =>
      api.pages.scans(siteId!, pageId!, {
        limit: SCAN_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextBefore,
    enabled: !!siteId && !!pageId,
    // Scans run daily; nothing changes under a minute.
    staleTime: 60_000,
  });
}

export function usePageScan(
  siteId: string | undefined,
  pageId: string | undefined,
  scanId: string | null,
) {
  return useQuery({
    queryKey: ["sites", siteId ?? "", "pages", pageId ?? "", "scans", scanId],
    queryFn: () => api.pages.scan(siteId!, pageId!, scanId!),
    enabled: !!siteId && !!pageId && !!scanId,
    // A stored scan never changes.
    staleTime: Infinity,
  });
}

/** Changes fetched per page of `Headers › Changes` (task 374). */
export const CHANGE_PAGE_SIZE = 50;

/** The window the tab row's badge counts over. */
export const CHANGE_BADGE_DAYS = 7;

/** One site's scans that recorded a change, newest first, by `nextCursor`. */
/**
 * What Headers › Changes is narrowed to (task 449): the grade drops alone,
 * and the scans of the last `within`. The Overview's grade drops card links
 * to both. Resolved to a time at fetch, like the sections' ranges.
 */
export interface PageChangeFilter {
  gradeDropped?: boolean;
  within?: TimeRangePreset;
}

/**
 * The query's filters, with `within` resolved to a `startTime` now, or kept
 * as `startTime` when a later page passes the first page's back.
 */
function changeParams(filter: PageChangeFilter, startTime?: string) {
  const start =
    startTime ??
    (filter.within
      ? new Date(Date.now() - TIME_RANGE_MS[filter.within]).toISOString()
      : undefined);
  return {
    ...(filter.gradeDropped ? { gradeDropped: true } : {}),
    ...(start ? { startTime: start } : {}),
  };
}

export function usePageChanges(
  siteId: string | undefined,
  filter: PageChangeFilter = {},
) {
  return useInfiniteQuery({
    queryKey: ["sites", siteId ?? "", "page-changes", filter],
    queryFn: async ({ pageParam }) => {
      // Every page of one list reads the first page's window, so a later
      // page does not lose the rows the count included.
      const params = changeParams(filter, pageParam?.startTime);
      const page = await api.pages.changes(siteId!, {
        limit: CHANGE_PAGE_SIZE,
        ...params,
        ...(pageParam ? { cursor: pageParam.cursor } : {}),
      });
      return { ...page, startTime: params.startTime };
    },
    initialPageParam: null as {
      cursor: string;
      startTime: string | undefined;
    } | null,
    getNextPageParam: (last) =>
      last.nextCursor
        ? { cursor: last.nextCursor, startTime: last.startTime }
        : null,
    enabled: !!siteId,
    // Scans run daily; nothing changes under a minute.
    staleTime: 60_000,
  });
}

/**
 * The first page of every site's changes at once, for `Headers › Changes`
 * under "all sites" (ADR 013): one query per site, merged by the page. A
 * site with more than a page says so there rather than paging a merge.
 */
export function usePageChangesForSites(
  siteIds: readonly string[],
  filter: PageChangeFilter = {},
) {
  return useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["sites", siteId, "page-changes", "first", filter],
      queryFn: () =>
        api.pages.changes(siteId, {
          limit: CHANGE_PAGE_SIZE,
          ...changeParams(filter),
        }),
      staleTime: 60_000,
    })),
  });
}

/**
 * How many scans recorded a change in the last `CHANGE_BADGE_DAYS` days,
 * summed over `siteIds`, for the Changes tab's badge: the list's own query
 * with `limit=0`. Undefined until every site has answered, so the badge
 * never shows a partial sum.
 */
export function useWeeklyChangeCount(
  siteIds: readonly string[],
): number | undefined {
  const results = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: ["sites", siteId, "page-changes", "weekly-count"],
      queryFn: () =>
        api.pages.changes(siteId, {
          limit: 0,
          startTime: new Date(
            Date.now() - CHANGE_BADGE_DAYS * 24 * 3600 * 1000,
          ).toISOString(),
        }),
      staleTime: 60_000,
    })),
  });
  if (siteIds.length === 0 || results.some((result) => !result.data)) {
    return undefined;
  }
  return results.reduce((sum, result) => sum + (result.data?.count ?? 0), 0);
}
